import React, { createContext, useContext, useState, useEffect } from "react";
import { toast } from "sonner";

export interface Scenario {
  id: string;
  name: string;
  type:
    | "Personal Finance"
    | "Real Estate"
    | "Investment"
    | "Business Valuation"
    | "Annuity & Life"
    | "401(k) Rollover"
    | "Roth Conversion"
    | "Business Continuity";
  inputs: any;
  results: any;
  timestamp: number;
}

export interface Activity {
  id: string;
  action: string;
  details: string;
  timestamp: number;
}

export interface UserProfile {
  name: string;
  email: string;
  phone: string;
  company: string;
  advisorName: string;
  status: "Active" | "Pending Appointment" | "Locked";
  notificationPreferences: {
    emailAlerts: boolean;
    advisorUpdates: boolean;
  };
  avatarUrl?: string;
  theme?: "light" | "dark" | "system";
  accentColor?: "default" | "blue" | "green" | "gold";
  downloadedResources: string[];
}

interface ProfileState {
  profile: UserProfile;
  scenarios: Scenario[];
  activities: Activity[];
  securityLogs: Activity[];
  updateProfile: (updates: Partial<UserProfile>) => void;
  saveScenario: (scenario: Omit<Scenario, "id" | "timestamp">) => void;
  deleteScenario: (id: string) => void;
  renameScenario: (id: string, newName: string) => void;
  duplicateScenario: (id: string) => void;
  logActivity: (action: string, details: string) => void;
  logSecurityActivity: (action: string, details: string) => void;
  markResourceDownloaded: (title: string) => void;
}

const defaultProfile: UserProfile = {
  name: "Executive User",
  email: "executive@example.com",
  phone: "",
  company: "",
  advisorName: "Pending",
  status: "Pending Appointment",
  notificationPreferences: {
    emailAlerts: true,
    advisorUpdates: true,
  },
  avatarUrl: "",
  theme: "system",
  accentColor: "default",
  downloadedResources: [],
};

const ProfileContext = createContext<ProfileState | undefined>(undefined);

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const [profile, setProfile] = useState<UserProfile>(defaultProfile);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [securityLogs, setSecurityLogs] = useState<Activity[]>([]);

  useEffect(() => {
    const savedProfile = localStorage.getItem("srg_profile");
    const savedScenarios = localStorage.getItem("srg_scenarios");
    const savedActivities = localStorage.getItem("srg_activities");
    const savedSecurityLogs = localStorage.getItem("srg_security_logs");

    if (savedProfile) setProfile(JSON.parse(savedProfile));
    if (savedScenarios) setScenarios(JSON.parse(savedScenarios));
    if (savedActivities) setActivities(JSON.parse(savedActivities));
    if (savedSecurityLogs) setSecurityLogs(JSON.parse(savedSecurityLogs));
  }, []);

  useEffect(() => {
    const root = document.documentElement;

    // Apply theme (light/dark)
    const isDark =
      profile.theme === "dark" ||
      (profile.theme === "system" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches);
    root.classList.toggle("dark", isDark);

    // Apply accent color
    root.classList.remove("theme-blue", "theme-green", "theme-gold");
    if (profile.accentColor && profile.accentColor !== "default") {
      root.classList.add(`theme-${profile.accentColor}`);
    }
  }, [profile.theme, profile.accentColor]);

  const saveState = (
    p: UserProfile,
    s: Scenario[],
    a: Activity[],
    sl: Activity[],
  ) => {
    localStorage.setItem("srg_profile", JSON.stringify(p));
    localStorage.setItem("srg_scenarios", JSON.stringify(s));
    localStorage.setItem("srg_activities", JSON.stringify(a));
    localStorage.setItem("srg_security_logs", JSON.stringify(sl));
  };

  const updateProfile = (updates: Partial<UserProfile>) => {
    const newProfile = { ...profile, ...updates };
    setProfile(newProfile);
    saveState(newProfile, scenarios, activities, securityLogs);
  };

  const logActivity = (action: string, details: string) => {
    const newActivity: Activity = {
      id: crypto.randomUUID(),
      action,
      details,
      timestamp: Date.now(),
    };
    const newActivities = [newActivity, ...activities].slice(0, 50);
    setActivities(newActivities);
    saveState(profile, scenarios, newActivities, securityLogs);
  };

  const logSecurityActivity = (action: string, details: string) => {
    const newLog: Activity = {
      id: crypto.randomUUID(),
      action,
      details,
      timestamp: Date.now(),
    };
    const newLogs = [newLog, ...securityLogs].slice(0, 50);
    setSecurityLogs(newLogs);
    saveState(profile, scenarios, activities, newLogs);
  };

  const saveScenario = (scenarioInfo: Omit<Scenario, "id" | "timestamp">) => {
    const newScenario: Scenario = {
      ...scenarioInfo,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
    };
    const newScenarios = [newScenario, ...scenarios];
    setScenarios(newScenarios);
    logActivity(
      "Saved Scenario",
      `Saved ${scenarioInfo.type} scenario: ${scenarioInfo.name}`,
    );
    saveState(profile, newScenarios, activities, securityLogs);

    // Simulate email notification
    if (profile.notificationPreferences.emailAlerts) {
      toast.success(
        `Email confirmation sent for saved scenario: ${scenarioInfo.name}`,
      );
    }
  };

  const deleteScenario = (id: string) => {
    const scenario = scenarios.find((s) => s.id === id);
    if (scenario) {
      const newScenarios = scenarios.filter((s) => s.id !== id);
      setScenarios(newScenarios);
      logActivity("Deleted Scenario", `Deleted scenario: ${scenario.name}`);
      saveState(profile, newScenarios, activities, securityLogs);
    }
  };

  const renameScenario = (id: string, newName: string) => {
    const newScenarios = scenarios.map((s) =>
      s.id === id ? { ...s, name: newName } : s,
    );
    setScenarios(newScenarios);
    logActivity("Renamed Scenario", `Renamed scenario to: ${newName}`);
    saveState(profile, newScenarios, activities, securityLogs);
  };

  const duplicateScenario = (id: string) => {
    const scenarioToDuplicate = scenarios.find((s) => s.id === id);
    if (scenarioToDuplicate) {
      const newScenario: Scenario = {
        ...scenarioToDuplicate,
        id: crypto.randomUUID(),
        name: `${scenarioToDuplicate.name} (Copy)`,
        timestamp: Date.now(),
      };
      const newScenarios = [newScenario, ...scenarios];
      setScenarios(newScenarios);
      logActivity(
        "Duplicated Scenario",
        `Duplicated scenario: ${scenarioToDuplicate.name}`,
      );
      saveState(profile, newScenarios, activities, securityLogs);
    }
  };

  const markResourceDownloaded = (title: string) => {
    if (!profile.downloadedResources?.includes(title)) {
      const updatedResources = [...(profile.downloadedResources || []), title];
      updateProfile({ downloadedResources: updatedResources });
      logActivity("Downloaded Resource", `Downloaded resource: ${title}`);
    }
  };

  return (
    <ProfileContext.Provider
      value={{
        profile,
        scenarios,
        activities,
        securityLogs,
        updateProfile,
        saveScenario,
        deleteScenario,
        renameScenario,
        duplicateScenario,
        logActivity,
        logSecurityActivity,
        markResourceDownloaded,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }
  return context;
}
